import toPairs from "lodash/toPairs";

import { isValueAllowedInAction } from "../common/components/computation-types";
import type {
    DataReaderWriter,
    ColumnValues,
    DocumentData,
    AddRowToTableDocument,
    RowIndex,
    SetColumnsInRowDocument,
    DeleteRowDocument,
} from "../common/Database";
import type { TableName } from "../common/database-strings";
import { actionsMetadataCollectionForDevice, makeActionDocumentPath, ActionKind } from "../common/database-strings";
import { generateFirestoreDocumentID } from "../common/id-generator";

export interface ActionWithMetadataArguments {
    readonly jobID: string;
    readonly deviceID: string;
    readonly dependsOn: string | undefined;
    readonly appID: string;
    readonly appUserID: string | undefined;
    readonly authID: string | undefined;
}

async function postActionWithMetadata(
    db: DataReaderWriter,
    { jobID, deviceID, dependsOn, appUserID, authID }: ActionWithMetadataArguments,
    actionCollection: string,
    payload: DocumentData
): Promise<string> {
    let newJobID: boolean;
    if (jobID === undefined) {
        jobID = generateFirestoreDocumentID();
        newJobID = true;
    } else {
        newJobID = false;
    }
    const actionDoc: DocumentData = {
        ...payload,
        deviceID,
        writtenAt: db.database.serverTimestampFieldValue,
        receivedAt: false,
    };
    const metadataDoc: DocumentData = { deviceID, response: false };

    if (authID !== undefined) {
        actionDoc.authID = authID;
        metadataDoc.authID = authID;
    } else {
        metadataDoc.authID = false;
    }

    if (appUserID !== undefined) {
        actionDoc.appUserID = appUserID;
    }

    if (dependsOn !== undefined) {
        actionDoc.dependsOn = dependsOn;
    }

    const metadataPath = actionsMetadataCollectionForDevice(deviceID);

    if (newJobID || (await db.getDocument(metadataPath, jobID)) === undefined) {
        // We are reading, but throwing away, the action document before writing it.
        // This is due to Firestore transactions being based on optimistic locking
        // in the early 7.x series.
        //
        // Note that this will crash if the document already exists, based on
        // the way the security rule is written.

        // FIXME: We might not need the read here at all.
        // From Dan: The Firestore docs are not clear at all which documents need
        // to be read and which documents do not. I assumed that all written
        // documents needed to be read in the first place in a client-side
        // transaction when I wrote this. If we can post actions without crashes
        // by not reading the actionCollection then we don't need to read it at all.
        if (!newJobID) {
            await db.getDocument(actionCollection, jobID);
        }

        await db.setDocument(metadataPath, jobID, metadataDoc);
        await db.setDocument(actionCollection, jobID, actionDoc);
    }

    return jobID;
}

export interface AddRowActionWithMetadataArguments extends ActionWithMetadataArguments {
    readonly tableName: TableName;
    readonly columnValues: ColumnValues;
    readonly fromBuilder: boolean;
    readonly fromDataEditor: boolean;
}

export function cleanupColumnValues(columnValues: ColumnValues): ColumnValues {
    const cleaned: ColumnValues = {};
    for (const [k, v] of toPairs(columnValues)) {
        if (!isValueAllowedInAction(v)) continue;
        cleaned[k] = v;
    }
    return cleaned;
}

export async function postAddRowToTableActionWithMetadata(
    db: DataReaderWriter,
    { tableName, columnValues, fromBuilder, fromDataEditor, ...args }: AddRowActionWithMetadataArguments
): Promise<string> {
    columnValues = cleanupColumnValues(columnValues);
    const payload: AddRowToTableDocument = { tableName, columnValues, fromBuilder, fromDataEditor };
    return await postActionWithMetadata(
        db,
        args,
        makeActionDocumentPath(args.appID, ActionKind.AddRowToTable),
        payload
    );
}

export async function postSetColumnsInRowActionWithMetadata(
    db: DataReaderWriter,
    {
        tableName,
        columnValues,
        rowIndex,
        fromBuilder,
        fromDataEditor,
        ...args
    }: ActionWithMetadataArguments & {
        tableName: TableName;
        columnValues: ColumnValues;
        rowIndex: RowIndex;
        fromBuilder: boolean;
        fromDataEditor: boolean;
    }
): Promise<string> {
    columnValues = cleanupColumnValues(columnValues);
    const payload: SetColumnsInRowDocument = { tableName, rowIndex, columnValues, fromBuilder, fromDataEditor };
    return await postActionWithMetadata(
        db,
        args,
        makeActionDocumentPath(args.appID, ActionKind.SetColumnsInRow),
        payload
    );
}

export async function postDeleteRowActionWithMetadata(
    db: DataReaderWriter,
    {
        tableName,
        rowIndex,
        fromBuilder,
        fromDataEditor,
        ...args
    }: ActionWithMetadataArguments & {
        tableName: TableName;
        rowIndex: RowIndex;
        fromBuilder: boolean;
        fromDataEditor: boolean;
    }
): Promise<string> {
    const payload: DeleteRowDocument = { tableName, rowIndex: [rowIndex], fromBuilder, fromDataEditor };
    return await postActionWithMetadata(db, args, makeActionDocumentPath(args.appID, ActionKind.DeleteRow), payload);
}
